/**
 * Edit Preview Styles
 *
 * Centralized styles for the auto-redline preview components
 * Following the design specifications from PreviewChanges.md
 * Optimized for Word Add-in task pane constraints (320px min width)
 */

import { CSSProperties } from "react";
import { EditStatus } from "../../../interfaces/autoRedlineTypes";

/**
 * Container styles for the edit preview list
 */
export const listContainerStyle: CSSProperties = {
  marginTop: "12px",
  // No width set - inherits from parent
};

export const listHeaderStyle: CSSProperties = {
  fontSize: "18px",
  fontWeight: 700,
  color: "#333",
  marginBottom: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
};

/**
 * Composer Mode indicator badge
 */
export const composerModeIndicatorStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "14px",
  fontWeight: 500,
  color: "#106ebe",
  marginLeft: "auto",
};

/**
 * Base container style for individual edit preview
 */
export const containerStyle: CSSProperties = {
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "#e0e0e0",
  borderRadius: "8px",
  padding: "12px",
  marginTop: "8px",
  backgroundColor: "#f9f9f9",
  transition: "all 0.2s ease",
  position: "relative",

  // CRITICAL: Ensure width works in 320px constraint
  width: "100%",
  boxSizing: "border-box",
};

/**
 * Get dynamic container style based on status and hover state
 */
export const getContainerStyle = (status: EditStatus, isHovered: boolean): CSSProperties => {
  let style = { ...containerStyle };

  // Hover state (only when pending)
  if (isHovered && status === "pending") {
    style.backgroundColor = "#f5f5f5";
    style.borderColor = "#d0d0d0";
    style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.05)";
  }

  // Status-based colors
  switch (status) {
    case "applying":
      style.backgroundColor = "#f0f8ff"; // Light blue
      style.borderColor = "#0078d4"; // Microsoft blue
      break;
    case "applied":
      style.backgroundColor = "#f0fff4"; // Light green
      style.borderColor = "#28a745"; // Success green
      break;
    case "failed":
      style.backgroundColor = "#fff5f5"; // Light red
      style.borderColor = "#dc3545"; // Error red
      break;
  }

  return style;
};

/**
 * Edit header style (shows edit ID)
 */
export const headerStyle: CSSProperties = {
  fontSize: "12px",
  color: "#666",
  marginBottom: "8px",
  fontWeight: 500,

  // Handle long edit IDs
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/**
 * Diff container style
 */
export const diffContainerStyle: CSSProperties = {
  backgroundColor: "#fff",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "#e8e8e8",
  borderRadius: "4px",
  padding: "8px 12px",
  marginBottom: "8px",

  // CRITICAL: Constrain height for long diffs
  maxHeight: "200px",
  overflowY: "auto",

  // Ensure text wraps properly
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",

  // Font settings for readability
  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  fontSize: "14px",
  lineHeight: "1.6",
};

/**
 * Two-column row rendered INSIDE the diff container when the output carries a
 * numberingPreview: narrow native-numbering gutter on the left, paragraph
 * preview on the right. Top-aligned so the label lines up with the first line
 * of the preview text. When numberingPreview is null/absent the row is not
 * rendered at all (no empty gutter — single-column layout unchanged).
 */
export const numberedPreviewRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "10px",
};

/**
 * Left numbering column. Sized by its content (labels are short, e.g. "2.b."
 * or old→new redline markup); never shrinks so the text column wraps instead.
 * The subtle right border visually separates the gutter from the editable
 * paragraph text (same divider colour as the diff container border).
 */
export const numberingColumnStyle: CSSProperties = {
  flexShrink: 0,
  whiteSpace: "nowrap",
  paddingRight: "8px",
  borderRight: "1px solid #e8e8e8",
};

/**
 * Right text column of the numbered preview row — takes the remaining width.
 * minWidth 0 lets long unbreakable content wrap instead of overflowing the row.
 */
export const numberedPreviewTextStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

/**
 * Wrapper for the OUTPUT-LEVEL `numberingChange` gutter — a Word-Ribbon-style
 * Fluent icon (+ optional echoed prior-state label) rendered INSTEAD of the
 * `numberingPreview` HTML. Vertically stacks the icon above the prior-state
 * label; insertion-green (#00a86b) to match the "gained numbering" convention
 * used by `previewText` / `numberingPreview`.
 */
export const numberingChangeIndicatorStyle: CSSProperties = {
  display: "inline-flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "2px",
  color: "#00a86b",
};

/**
 * Echoed pre-change label ("was 2.b.") shown under the icon. Muted + smaller so
 * it reads as prior state, never as the current numbering label. Colour is
 * #595959 (~7:1 on white and on the #f5f5f5 empty-state block) so it still meets
 * WCAG AA for this small text while staying visibly muted.
 */
export const numberingChangeCurrentLabelStyle: CSSProperties = {
  fontSize: "11px",
  color: "#595959",
  whiteSpace: "nowrap",
};

/**
 * Visual diff styles - DEPRECATED
 * The backend provides HTML with inline styles that include author-specific colors
 * We should not override these with our own styles
 *
 * NOTE: Kept for reference only - DO NOT USE
 * The previewText HTML from backend already contains:
 * - <s style="color:#XXX">deleted text</s> with author-specific colors
 * - <u style="color:#XXX">inserted text</u> with author-specific colors
 */
// export const deletionStyle: CSSProperties = {
//   textDecoration: "line-through",
//   color: "#dc3545", // Red for deletions
//   backgroundColor: "#ffecec", // Light red background
// };

// export const insertionStyle: CSSProperties = {
//   textDecoration: "underline",
//   color: "#28a745", // Green for insertions
//   backgroundColor: "#e6ffed", // Light green background
// };

/**
 * Button container style
 */
export const buttonContainerStyle: CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: "8px",
  marginTop: "8px",
};

/**
 * Apply button base style
 */
export const applyButtonStyle: CSSProperties = {
  // Size constraints for narrow pane
  minWidth: "80px",
  height: "32px",
  fontSize: "14px",
  fontWeight: 500,

  // Visual design
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "#0078d4",
  backgroundColor: "#0078d4",
  color: "#fff",
  borderRadius: "4px",
  cursor: "pointer",
  transition: "all 0.2s ease",

  // Flexbox for icon + text
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  padding: "0 16px",

  // Remove default button styles
  outline: "none",
  border: "none",
};

/**
 * Get button style based on status
 */
export const getButtonStyle = (status: EditStatus): CSSProperties => {
  const baseStyle = { ...applyButtonStyle };

  switch (status) {
    case "applying":
      return {
        ...baseStyle,
        backgroundColor: "#0078d4",
        borderColor: "#0078d4",
        cursor: "not-allowed",
        opacity: 0.8,
      };
    case "applied":
      return {
        ...baseStyle,
        backgroundColor: "#28a745",
        borderColor: "#28a745",
        cursor: "default",
      };
    case "failed":
      return {
        ...baseStyle,
        backgroundColor: "#dc3545",
        borderColor: "#dc3545",
        cursor: "not-allowed", // Terminal — failed edits are not re-applied
      };
    default: // pending
      return baseStyle;
  }
};

/**
 * Button hover styles
 */
export const buttonHoverStyle: CSSProperties = {
  backgroundColor: "#106ebe",
  borderColor: "#106ebe",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12)",
};

/**
 * Error message style
 */
export const errorMessageStyle: CSSProperties = {
  color: "#dc3545",
  fontSize: "12px",
  fontStyle: "italic",
  marginRight: "auto", // Push to left side
};

/** Style for composer mode enabled button */
export const composerModeButtonStyle: CSSProperties = {
  background: "linear-gradient(135deg, #0066cc, #106ebe)",
  color: "white",
  border: "none",
  padding: "8px 16px",
  borderRadius: "6px",
  fontSize: "14px",
  fontWeight: 500,
  cursor: "not-allowed",
  opacity: 0.9,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  transition: "opacity 0.2s ease",
  minWidth: "140px",
};

/**
 * Animation keyframes (to be added to global CSS or inline styles)
 */
export const animationKeyframes = `
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  
  @keyframes pulse {
    0% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.9;
      transform: scale(1.02);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  @keyframes composer-orbit {
    from { 
      transform: rotate(0deg) translateX(6px) rotate(0deg);
    }
    to { 
      transform: rotate(360deg) translateX(6px) rotate(-360deg);
    }
  }
  
  @keyframes composer-orbit-reverse {
    from { 
      transform: rotate(0deg) translateX(6px) rotate(0deg);
    }
    to { 
      transform: rotate(-360deg) translateX(6px) rotate(360deg);
    }
  }
`;

// For backward compatibility
export const spinnerKeyframes = animationKeyframes;

/**
 * Spinner style
 */
export const spinnerStyle: CSSProperties = {
  width: "14px",
  height: "14px",
  border: "2px solid #fff",
  borderTopColor: "transparent",
  borderRadius: "50%",
  animation: "spin 0.8s linear infinite",
};

/**
 * Auto-apply status message style
 */
export const autoApplyStatusStyle: CSSProperties = {
  fontSize: "11px",
  color: "#666",
  fontStyle: "italic",
  marginTop: "4px",
};

/**
 * Progress indicator style for bulk operations
 */
export const progressIndicatorStyle: CSSProperties = {
  fontSize: "12px",
  color: "#0078d4",
  fontStyle: "italic",
  padding: "8px",
  backgroundColor: "#e6f2ff",
  borderRadius: "4px",
  marginTop: "8px",
  textAlign: "center",
};

/**
 * Empty state style (when no edits)
 */
export const emptyStateStyle: CSSProperties = {
  padding: "20px",
  textAlign: "center",
  color: "#999",
  fontSize: "13px",
  fontStyle: "italic",
};

/**
 * Edit list container style
 */
export const editListContainerStyle: CSSProperties = {
  marginTop: "8px",
  paddingTop: "8px",
  borderTop: "1px solid #e8e8e8",
  fontSize: "12px",
  color: "#666",
};

/**
 * Edit list header style
 */
export const editListHeaderStyle: CSSProperties = {
  fontWeight: 600,
  marginBottom: "4px",
};

/**
 * Edit list style
 */
export const editListStyle: CSSProperties = {
  margin: "0",
  paddingLeft: "20px",
  maxHeight: "150px",
  overflowY: "auto",
};

/**
 * Edit list item style
 */
export const editListItemStyle: CSSProperties = {
  marginBottom: "4px",
  display: "flex",
  alignItems: "flex-start",
  gap: "8px",
};

/**
 * Get edit status icon style
 */
export const getEditStatusIconStyle = (status: EditStatus): CSSProperties => ({
  minWidth: "16px",
  color:
    status === "applied"
      ? "#28a745"
      : status === "failed"
        ? "#dc3545"
        : status === "applying"
          ? "#0078d4"
          : "#999",
  fontWeight: "bold",
});

/**
 * Get edit description style
 */
export const getEditDescriptionStyle = (status: EditStatus): CSSProperties => ({
  flex: 1,
  color: status === "failed" ? "#dc3545" : "#333",
  wordBreak: "break-word",
});

/**
 * Edit error message style
 */
export const editErrorStyle: CSSProperties = {
  display: "block",
  fontSize: "11px",
  color: "#dc3545",
  marginTop: "2px",
};

/**
 * Header counter style
 */
export const headerCounterStyle: CSSProperties = {
  fontSize: "12px",
  color: "#666",
  fontWeight: "normal",
};

/**
 * Success counter style
 */
export const successCounterStyle: CSSProperties = {
  color: "#28a745",
};

/**
 * Expand button style
 */
export const expandButtonStyle: CSSProperties = {
  background: "none",
  border: "none",
  color: "#0078d4",
  cursor: "pointer",
  fontSize: "12px",
  padding: "0",
  marginLeft: "auto",
};

/**
 * Get apply button style with all states
 */
export const getApplyButtonStyle = (
  isComplete: boolean,
  hasFailed: boolean,
  failedCount: number,
  totalCount: number,
  isApplying: boolean,
  isReadOnly: boolean,
  isStreaming: boolean
): CSSProperties => ({
  ...applyButtonStyle,
  opacity: isReadOnly || isStreaming ? 0.5 : 1,
  cursor: isReadOnly || isStreaming || isComplete || isApplying ? "not-allowed" : "pointer",
  backgroundColor: isComplete
    ? "#28a745"
    : hasFailed
      ? failedCount === totalCount
        ? "#dc3545"
        : "#ffc107"
      : isApplying
        ? "#0078d4"
        : "#0078d4",
  borderColor: isComplete
    ? "#28a745"
    : hasFailed
      ? failedCount === totalCount
        ? "#dc3545"
        : "#ffc107"
      : "#0078d4",
  fontWeight: isComplete || hasFailed ? "600" : "500",
  boxShadow: isComplete
    ? "0 2px 4px rgba(40, 167, 69, 0.3)"
    : hasFailed
      ? "0 2px 4px rgba(220, 53, 69, 0.3)"
      : "none",
  transition: "all 0.3s ease",
  animation: isApplying ? "pulse 1.5s infinite" : "none",
});
